/**
 * Header.jsx
 * components/user/Header.jsx
 * — updated to wire all page sections
 */

import { useState, useEffect } from "react";
import BookAppointmentModal from "./BookAppointmentModal";
import { useUserAuth } from "../../context/UserAuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../icons/logo2.png";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Doctors", path: "/doctors" },
  { label: "Services", path: "/services" },
  { label: "Feedback", path: "/feedback" },
  { label: "Gallery", path: "/gallery" },
  { label: "Contact Us", path: "/contact" },
];

export default function Header() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("/");
  const location = useLocation();
  const { logout, isLoggedIn } = useUserAuth();
  const from = location.state?.from || "/";

  /* Shrink header on scroll */
  useEffect(() => {
    const checkHeader = () => {
      if (window.innerWidth <= 768) {
        // Always shrink on mobile
        setScrolled(true);
      } else {
        // Normal desktop scroll behavior
        setScrolled(window.scrollY > 60);
      }
    };

    checkHeader();

    window.addEventListener("scroll", checkHeader);
    window.addEventListener("resize", checkHeader);

    return () => {
      window.removeEventListener("scroll", checkHeader);
      window.removeEventListener("resize", checkHeader);
    };
  }, []);

  /* Highlight active section on scroll */
  // useEffect(() => {
  //   const ids = NAV_LINKS.map((l) => l.href.replace("#", "")).filter(Boolean);
  //   const observer = new IntersectionObserver(
  //     (entries) => {
  //       entries.forEach((entry) => {
  //         if (entry.isIntersecting) setActiveLink("#" + entry.target.id);
  //       });
  //     },
  //     { rootMargin: "-40% 0px -55% 0px" },
  //   );
  //   ids.forEach((id) => {
  //     const el = document.getElementById(id);
  //     if (el) observer.observe(el);
  //   });
  //   return () => observer.disconnect();
  // }, []);

  const handleNavClick = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate(from, { replace: true });
  };

  return (
    <>
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          fontFamily: "'Nunito','Segoe UI',sans-serif",
          boxShadow: "0 4px 24px rgba(0,0,0,0.45)",
          transition: "all .3s ease",
        }}
      >
        {/* ── ROW 1 : Brand Bar ── */}
        <div
          style={{
            background: scrolled ? "rgba(8,18,38,0.99)" : "rgba(8,18,38,0.95)",
            backdropFilter: "blur(14px)",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            transition: "background .3s",
          }}
        >
          <div
            style={{
              maxWidth: 1200,
              margin: "0 auto",
              padding: scrolled ? "8px 1.5rem" : "12px 1.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "padding .3s",
            }}
          >
            {/* Left: Circular PNG Logo */}
            <div
              style={{
                width: scrolled ? 50 : 70,
                height: scrolled ? 50 : 70,
                borderRadius: "50%",
                overflow: "hidden",
                border: "2px solid rgba(255,255,255,0.2)",
                boxShadow: "0 0 12px rgba(74,127,212,0.4)",
                // background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all .3s ease",
              }}
            >
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("/");
                }}
                style={{ display: "block", width: "100%", height: "100%" }}
              >
                <img
                  src={logo}
                  alt="Clinic Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </a>
            </div>

            {/* Center: Clinic name + tagline */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("/");
                }}
                style={{ textDecoration: "none" }}
              >
                <div
                  style={{
                    fontSize: scrolled ? 26 : 36,
                    fontWeight: 900,
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: "#4a7fd4",
                    textShadow:
                      "2px 2px 0px #1a2f6e, -1px -1px 0px #1a2f6e, 1px -1px 0px #1a2f6e, -1px 1px 0px #1a2f6e, 0 0 18px rgba(74,127,212,0.4)",
                    lineHeight: 1.1,
                    transition: "font-size .3s",
                    fontFamily: "'Nunito','Arial Black',sans-serif",
                  }}
                >
                  OM DENTAL CLINIC
                </div>
                {/* {!scrolled && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: "1.8px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.82)",
                    }}
                  >
                    Advanced Implant{" "}
                    <span style={{ color: "#E8323C" }}>&amp;</span> Smile Design
                    Center
                  </div>
                )} */}
              </a>
            </div>

            {/* Right: Logout + Hamburger */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setModalOpen(true)}
                className="d-none d-md-inline-flex align-items-center gap-2"
                style={{
                  background: "#E8323C",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "9px 18px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  letterSpacing: ".3px",
                  boxShadow: "0 4px 14px rgba(232,50,60,0.35)",
                  transition: "all .2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#C82030";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#E8323C";
                  e.currentTarget.style.transform = "none";
                }}
              >
                📅 Book Appointment
              </button>
              {isLoggedIn ? (
                <button
                  onClick={handleLogout}
                  className="d-none d-md-inline-flex align-items-center gap-2"
                  style={{
                    background: "#444",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "9px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  🚪 Logout
                </button>
              ) : (
                <button
                  onClick={() => navigate("/login")}
                  className="d-none d-md-inline-flex align-items-center gap-2"
                  style={{
                    background: "#1D9E75",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "9px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  🔑 Login
                </button>
              )}
              {/* Hamburger — mobile only */}
              <button
                className="d-lg-none"
                onClick={() => setMobileOpen(!mobileOpen)}
                style={{
                  background: "rgba(255,255,255,.1)",
                  border: "1px solid rgba(255,255,255,.15)",
                  borderRadius: 6,
                  padding: "6px 10px",
                  color: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                {mobileOpen ? "✕" : "☰"}
              </button>
            </div>
          </div>
        </div>

        {/* ── ROW 2 : Nav Bar (desktop) ── */}
        <div
          className="d-none d-lg-block"
          style={{
            background: "rgba(20,40,80,0.97)",
            backdropFilter: "blur(10px)",
            borderBottom: "2px solid rgba(74,127,212,0.35)",
          }}
        >
          <nav
            style={{
              maxWidth: 1200,
              margin: "0 auto",
              padding: "0 1.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <a
                  key={link.path}
                  href={link.path}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.path);
                  }}
                  style={{
                    padding: "11px 20px",
                    fontSize: 13,
                    fontWeight: 700,
                    textDecoration: "none",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: isActive ? "#fff" : "rgba(255,255,255,0.72)",
                    background: isActive
                      ? "rgba(74,127,212,0.3)"
                      : "transparent",
                    borderBottom: isActive
                      ? "3px solid #4a7fd4"
                      : "3px solid transparent",
                    transition: "all .2s",
                    display: "inline-block",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.background =
                        "rgba(74,127,212,0.15)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = "rgba(255,255,255,0.72)";
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  {link.label}
                </a>
              );
            })}
          </nav>
        </div>

        {/* ── Mobile menu ── */}
        {mobileOpen && (
          <div
            style={{
              background: "rgba(8,18,38,0.99)",
              borderTop: "1px solid rgba(255,255,255,.08)",
              padding: "1rem 1.5rem",
            }}
          >
            {NAV_LINKS.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.path);
                }}
                style={{
                  display: "block",
                  padding: "10px 0",
                  fontSize: 14,
                  fontWeight: 600,
                  color:
                    location.pathname === link.path
                      ? "#4a7fd4"
                      : "rgba(255,255,255,.75)",
                  textDecoration: "none",
                  borderBottom: "1px solid rgba(255,255,255,.06)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {link.label}
              </a>
            ))}
            {/* <button
              onClick={() => {
                setMobileOpen(false);
                setModalOpen(true);
              }}
              style={{
                marginTop: 12,
                width: "100%",
                background: "#E8323C",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "11px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              📅 Book Appointment
            </button> */}
            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                style={{
                  marginTop: 8,
                  width: "100%",
                  background: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "11px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                🚪 Logout
              </button>
            ) : (
              <button
                onClick={() => navigate("/login")}
                style={{
                  marginTop: 8,
                  width: "100%",
                  background: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "11px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                🔑 Login
              </button>
            )}
          </div>
        )}
      </header>

      <BookAppointmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
